import { create } from 'zustand';

/**
 * Socket Connection State - managed by SocketContext
 *
 * This store ONLY contains connection state, not the socket instance itself.
 * Socket instance lives in SocketContext and is accessed via useSocket() hook.
 *
 * Real-time data stores:
 * - Timer data → useTimerStore
 * - Payment data → usePaymentRealtimeStore
 */
interface SocketState {
  // Connection state
  isConnected: boolean;
  error: string | null;
  connectionAttempts: number;
  isReconnecting: boolean;
  reconnectAttempt: number;

  // Actions - called by SocketContext
  setConnected: (connected: boolean) => void;
  setError: (error: string | null) => void;
  incrementConnectionAttempts: () => void;
  setReconnecting: (reconnecting: boolean) => void;
  setReconnectAttempt: (attempt: number) => void;
  resetReconnectAttempt: () => void;
  resetError: () => void;
}

export const useSocketStore = create<SocketState>((set) => ({
  // Initial state
  isConnected: false,
  error: null,
  connectionAttempts: 0,
  isReconnecting: false,
  reconnectAttempt: 0,

  // Actions
  setConnected: (connected) => set({ isConnected: connected }),

  setError: (error) =>
    set((state) => {
      // Prevent setting identical error to avoid infinite loops
      if (state.error === error) {
        return state;
      }
      return { error };
    }),

  incrementConnectionAttempts: () =>
    set((state) => ({
      connectionAttempts: state.connectionAttempts + 1,
    })),

  setReconnecting: (reconnecting) => set({ isReconnecting: reconnecting }),

  setReconnectAttempt: (attempt) => set({ reconnectAttempt: attempt }),

  resetReconnectAttempt: () => set({ reconnectAttempt: 0 }),

  resetError: () => set({ error: null }),
}));
